import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import {
  ArrowLeft, Check, Clock, Construction, Sparkles, Database,
  Bot, FileText, ScanLine, Bell, HelpCircle, Wallet, ListTodo, FolderOpen,
  CreditCard, Building2, Shield, Trophy, Crown, Gift, Euro,
} from 'lucide-react';

const sections = {
  fait: [
    {
      icon: Shield,
      title: 'Authentification & sécurité',
      items: [
        'Inscription / connexion JWT',
        "Panel administrateur protégé",
      ],
    },
    {
      icon: HelpCircle,
      title: 'Simulateur d\'aides',
      items: [
        'Profil utilisateur (âge, situation, revenus, loyer)',
        'Calcul APL, RSA, Bourse CROUS, CSS, Chèque énergie',
        'Barèmes 2025 réels (OpenFisca + fallback local)',
        'Génération automatique des tâches associées',
      ],
    },
    {
      icon: Wallet,
      title: 'Simulateur d\'impôts',
      items: [
        'Calcul IR avec barèmes 2025',
        'Quotient familial',
        'Décote, plafonnement, abattements',
      ],
    },
    {
      icon: FolderOpen,
      title: 'Coffre-fort documents',
      items: [
        'Upload PDF / images / Word (10 Mo max)',
        'Catégorisation (impôts, logement, travail, santé, banque, identité)',
        'Téléchargement, modification, suppression',
        'Recherche dans les documents',
      ],
    },
    {
      icon: ScanLine,
      title: 'OCR — Extraction de texte',
      items: [
        'Tesseract OCR (français + anglais)',
        'Support PDF (via Poppler) et images',
        'Détection auto : montants, dates, RFR, loyer, salaire net',
        'Identification du type de document (avis d\'imposition, bulletin de paie, bail…)',
      ],
    },
    {
      icon: FileText,
      title: 'Lettres Magiques (IA)',
      items: [
        '5 modèles : résiliation, réclamation, demande d\'aide, attestation, mise en demeure',
        'Génération GPT-5.2 personnalisée (Emergent LLM)',
        'Téléchargement PDF',
        'Historique des lettres générées',
      ],
    },
    {
      icon: Bell,
      title: 'Rappels',
      items: [
        'Création / édition / suppression de rappels',
        'Vue groupée : à venir / en retard / terminés',
        'Calendrier fiscal 2026 pré-rempli en 1 clic',
        'Catégories : impôts, aides, documents, abonnements',
      ],
    },
    {
      icon: CreditCard,
      title: 'Abonnements',
      items: [
        'Liste avec coût mensuel / annuel total',
        'Détection de doublons (économies potentielles)',
        'Ajout manuel ou via Gmail (à finaliser)',
        '10 abonnements de démo pour tester',
      ],
    },
    {
      icon: Bot,
      title: 'Chatbot IA',
      items: [
        'Conversation contextuelle GPT-5.2',
        'Création automatique de tâches via [TASK:...]',
        'Historique multi-sessions',
      ],
    },
    {
      icon: ListTodo,
      title: 'Tâches & Finances',
      items: [
        'CRUD complet sur les tâches (priorité, deadline)',
        'Gestion des entrées/sorties',
        'Import CSV des transactions',
        'Calcul du solde',
      ],
    },
    {
      icon: Trophy,
      title: 'Gamification',
      items: [
        '15 badges (onboarding, aides, documents, lettres, rappels, streaks)',
        '8 niveaux (Débutant → Légende) basés sur l\'XP',
        'Streaks quotidiennes (3j, 7j, 30j)',
        'Toast d\'unlock + page /dashboard/recompenses',
      ],
    },
    {
      icon: Crown,
      title: 'Abonnement Freemium',
      items: [
        '3 plans : Free, Plus (4,99€/mois), Pro (9,99€/mois)',
        'Limites par plan appliquées (aides, documents, OCR, lettres, chat)',
        'Carte "Ton utilisation actuelle" avec barres de progression',
        'Stripe en mode SIMULÉ (vraies clés à venir)',
      ],
    },
    {
      icon: Building2,
      title: 'Panel administrateur',
      items: [
        'Roadmap publique et privée',
        'CRUD complet sur les items de roadmap',
        'Statistiques par statut, priorité, catégorie',
      ],
    },
    {
      icon: Database,
      title: 'Architecture backend',
      items: [
        'FastAPI + MongoDB',
        'Modulaire : 11 routers (auth, aids, documents, letters, reminders, oauth, subscriptions, connections, admin, gamification, billing)',
        '49 tests automatisés (49/49 ✅)',
        'Hot reload, supervisor, logs centralisés',
      ],
    },
  ],
  enCours: [
    {
      icon: Construction,
      title: 'Vraie intégration Stripe',
      detail: 'Branchement du SDK Stripe officiel pour les paiements réels. Le code de base existe déjà (checkout, confirm, cancel) mais en mode simulé.',
      need: 'Clés Stripe (test + live)',
    },
    {
      icon: Construction,
      title: 'Gmail OAuth Production',
      detail: 'Synchronisation automatique des abonnements depuis la boîte Gmail. Code OAuth déjà écrit.',
      need: 'GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET',
    },
  ],
  aFaire: [
    {
      icon: Clock,
      title: 'FranceConnect',
      detail: 'Connexion via identité numérique officielle pour récupérer les données certifiées.',
      priority: 'P1',
    },
    {
      icon: Clock,
      title: 'Notifications email / push',
      detail: 'Envoi de rappels par email (J-7, J-1, J) — à brancher avec Resend ou SendGrid.',
      priority: 'P1',
    },
    {
      icon: Clock,
      title: 'Connexion bancaire (Bridge API)',
      detail: 'Importation automatique des transactions bancaires.',
      priority: 'P1',
    },
    {
      icon: Clock,
      title: 'Résiliation auto d\'abonnements',
      detail: 'Envoyer les lettres de résiliation directement depuis l\'app (via La Poste API).',
      priority: 'P2',
    },
    {
      icon: Clock,
      title: 'Comparateur d\'abonnements',
      detail: 'Suggestion d\'alternatives moins chères pour les services détectés.',
      priority: 'P2',
    },
    {
      icon: Clock,
      title: 'Système de parrainage',
      detail: 'Inviter des amis et gagner des crédits.',
      priority: 'P2',
    },
    {
      icon: Clock,
      title: 'PWA + app mobile',
      detail: 'Service worker, mode hors-ligne, puis app React Native.',
      priority: 'P2',
    },
    {
      icon: Clock,
      title: 'Bandeaux d\'alerte préventifs',
      detail: 'Prévenir l\'utilisateur quand il approche d\'une limite (ex: "Plus que 1 lettre / 2 ce mois").',
      priority: 'P2',
    },
  ],
  gratuit: [
    {
      title: 'Stack technique de base',
      detail: 'React, FastAPI, MongoDB, Tailwind, Shadcn/UI, Framer Motion',
      note: 'Open-source, déjà installé.',
    },
    {
      title: 'Tesseract OCR (français + anglais)',
      detail: 'Reconnaissance de texte sur documents PDF + images',
      note: 'Open-source, installé via apt-get. Aucun coût d\'utilisation.',
    },
    {
      title: 'OpenFisca (calcul des aides)',
      detail: 'Barèmes 2025 réels (APL, RSA, bourses, CSS, chèque énergie)',
      note: 'Logique de l\'État français, gratuite. Fallback local intégré.',
    },
    {
      title: 'Emergent LLM Key (GPT-5.2, Gemini, Claude)',
      detail: 'Chatbot IA + génération des lettres administratives',
      note: 'Clé universelle Emergent — déjà active, recharges via Profil → Universal Key.',
    },
    {
      title: 'Hébergement preview Emergent',
      detail: 'URL de prévisualisation, hot reload, supervisor, MongoDB local',
      note: 'Inclus dans la plateforme Emergent.',
    },
    {
      title: 'Génération PDF (Reportlab)',
      detail: 'Export PDF des lettres magiques',
      note: 'Open-source, déjà installé.',
    },
  ],
  payant: [
    {
      title: 'Stripe (paiements Freemium)',
      detail: 'Encaissement réel des abonnements Plus / Pro.',
      cost: '1,4% + 0,25€ par transaction européenne, 0€/mois',
      who: 'Toi (compte Stripe) — clés test gratuites, mise en prod après vérif KYC.',
      blockedBy: 'Clés Stripe (test + live)',
    },
    {
      title: 'Gmail API (scan des abonnements)',
      detail: 'Lecture des emails pour détecter Netflix, Spotify, etc.',
      cost: 'Gratuit jusqu\'à 1 milliard de requêtes/jour — donc 0€ en pratique',
      who: 'Toi (Google Cloud Console) — création projet OAuth nécessaire.',
      blockedBy: 'GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET',
    },
    {
      title: 'SendGrid / Resend (emails de rappels)',
      detail: 'Envoi automatique des rappels fiscaux par email (J-7, J-1, J).',
      cost: 'Gratuit jusqu\'à 100 emails/jour (SendGrid) ou 3000/mois (Resend) puis ~15€/mois',
      who: 'Toi (compte SendGrid ou Resend).',
      blockedBy: 'Clé API + domaine vérifié',
    },
    {
      title: 'Bridge API (connexion bancaire)',
      detail: 'Importation auto des transactions bancaires (Bridge by Bankin\').',
      cost: '~0,50–1€ par utilisateur connecté / mois (sur devis)',
      who: 'Toi — contrat B2B avec Bridge, signature DSP2 nécessaire.',
      blockedBy: 'Contrat Bridge + clés API',
    },
    {
      title: 'La Poste API (envoi courriers)',
      detail: 'Envoyer les lettres magiques en recommandé directement depuis l\'app.',
      cost: '~5–8€ par courrier recommandé envoyé',
      who: 'Utilisateur final (refacturé) — compte API La Poste à créer.',
      blockedBy: 'Compte développeur La Poste',
    },
    {
      title: 'FranceConnect',
      detail: 'Connexion via identité numérique officielle.',
      cost: '0€ (service public)',
      who: 'Toi — demande d\'agrément auprès de la DINUM (3-6 semaines).',
      blockedBy: 'Agrément FranceConnect',
    },
    {
      title: 'Hébergement production (après le preview)',
      detail: 'Domaine personnalisé, scaling, MongoDB Atlas',
      cost: 'Domaine ~10€/an, Atlas Free tier puis ~9€/mois si volume',
      who: 'Toi — quand la beta sera prête à passer en production.',
      blockedBy: 'Choix : Emergent prod, Vercel + Railway, ou autre',
    },
  ],
};

// Public-friendly status overview (no jargon)
const publicStatus = [
  { label: 'Simulateur d\'aides', status: 'disponible' },
  { label: 'Coffre-fort documents', status: 'disponible' },
  { label: 'Lecture automatique des documents (OCR)', status: 'disponible' },
  { label: 'Lettres administratives automatiques', status: 'disponible' },
  { label: 'Rappels (dates fiscales, démarches)', status: 'disponible' },
  { label: 'Suivi des abonnements', status: 'en-test' },
  { label: 'Connexion Gmail (détection d\'abonnements)', status: 'bientot' },
  { label: 'Paiements & abonnement payant', status: 'bientot' },
  { label: 'Notifications par email', status: 'bientot' },
  { label: 'FranceConnect', status: 'prevu' },
  { label: 'Connexion bancaire (Bridge)', status: 'prevu' },
  { label: 'App mobile', status: 'prevu' },
];

const statusLabel = {
  disponible: { text: 'Disponible', color: '#81d8d0', dot: '●' },
  'en-test': { text: 'En test', color: '#f59e0b', dot: '◐' },
  bientot: { text: 'Bientôt', color: '#a1a1a6', dot: '○' },
  prevu: { text: 'Prévu', color: '#a1a1a6', dot: '○' },
};

export const RecapPage = () => {
  const { isDark } = useTheme();
  // Technical detail mode toggled via ?dev=1 (for developers / investors)
  const isDevMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('dev');

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0F0F1A] text-white' : 'bg-[#FAFBFC] text-black'}`} data-testid="recap-page">
      {/* Header */}
      <div className={`sticky top-0 z-10 backdrop-blur-xl ${isDark ? 'bg-[#0F0F1A]/80 border-white/10' : 'bg-white/80 border-black/10'} border-b`}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-70 transition" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Accueil</span>
          </Link>
          <h1 className="text-lg font-medium">État du projet</h1>
          <div className="w-20" />
        </div>
      </div>

      {/* Public-friendly summary (always visible) */}
      {!isDevMode && (
        <div className="max-w-3xl mx-auto px-6 py-16" data-testid="public-status">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs uppercase tracking-widest bg-[#81d8d0]/15 text-[#81d8d0] mb-4">
              <Sparkles className="w-3 h-3" />
              Transparence
            </div>
            <h2 className="text-3xl sm:text-4xl font-light mb-4">Ce qui est déjà disponible.</h2>
            <p className="opacity-60 max-w-xl mx-auto">
              Skipya est en bêta. Voici simplement où on en est, sans jargon.
            </p>
          </motion.div>

          <div className="space-y-2">
            {publicStatus.map((item, i) => {
              const s = statusLabel[item.status];
              return (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03 }}
                  className={`flex items-center justify-between p-4 rounded-xl ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-black/5'} border`}
                  data-testid={`status-${i}`}
                >
                  <span className="text-sm">{item.label}</span>
                  <span className="text-xs flex items-center gap-2" style={{ color: s.color }}>
                    <span style={{ fontSize: '14px' }}>{s.dot}</span>
                    {s.text}
                  </span>
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-xs opacity-40 mt-12">
            Page mise à jour le 13/05/2026
          </p>
        </div>
      )}

      {/* Detailed/technical view (only with ?dev=1) */}
      {isDevMode && (
      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16 text-center"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs uppercase tracking-widest bg-[#81d8d0]/15 text-[#81d8d0] mb-4">
            <Sparkles className="w-3 h-3" />
            Transparence totale
          </div>
          <h2 className="text-4xl sm:text-5xl font-light mb-4">Où en est Skipya ?</h2>
          <p className="opacity-60 max-w-xl mx-auto">
            La V1 Beta est déjà fonctionnelle de bout en bout. Voici ce qui marche aujourd'hui, ce qui est en cours, et ce qui arrive bientôt.
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-16" data-testid="recap-stats">
          {[
            { value: sections.fait.length, label: 'Fonctionnalités livrées', color: '#81d8d0' },
            { value: sections.enCours.length, label: 'En cours de branchement', color: '#f59e0b' },
            { value: sections.aFaire.length, label: 'Sur la roadmap', color: '#94a3b8' },
            { value: sections.gratuit.length, label: 'Briques gratuites', color: '#81d8d0' },
            { value: sections.payant.length, label: 'Intégrations payantes', color: '#f59e0b' },
          ].map((s) => (
            <div
              key={s.label}
              className={`p-5 rounded-2xl text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'} border`}
            >
              <div className="text-3xl font-light" style={{ color: s.color }}>{s.value}</div>
              <div className="text-[10px] opacity-60 mt-1 uppercase tracking-widest">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Fait & fonctionnel */}
        <section className="mb-16" data-testid="section-fait">
          <div className="flex items-baseline gap-3 mb-8">
            <h3 className="text-2xl font-light">
              <span className="text-[#81d8d0]">●</span> Fait & fonctionnel
            </h3>
            <span className="text-xs opacity-50">{sections.fait.length} modules</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.fait.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03 }}
                  className={`p-5 rounded-2xl ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-black/5'} border`}
                  data-testid={`fait-${i}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 rounded-lg bg-[#81d8d0]/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-[#81d8d0]" />
                    </div>
                    <div className="font-medium pt-1">{card.title}</div>
                  </div>
                  <ul className="space-y-1.5 pl-1">
                    {card.items.map((it, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm opacity-80">
                        <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-[#81d8d0]" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* En cours */}
        <section className="mb-16" data-testid="section-en-cours">
          <div className="flex items-baseline gap-3 mb-8">
            <h3 className="text-2xl font-light">
              <span className="text-yellow-500">●</span> En cours de branchement
            </h3>
            <span className="text-xs opacity-50">{sections.enCours.length} en attente de clés</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.enCours.map((card, i) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className={`p-5 rounded-2xl ${isDark ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200'} border`}
                  data-testid={`en-cours-${i}`}
                >
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-yellow-500/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div>
                      <div className="font-medium">{card.title}</div>
                      <div className="text-xs opacity-60 mt-0.5">{card.detail}</div>
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest opacity-50 pl-12">
                    Attend : {card.need}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* À faire */}
        <section className="mb-16" data-testid="section-a-faire">
          <div className="flex items-baseline gap-3 mb-8">
            <h3 className="text-2xl font-light">
              <span className="opacity-50">●</span> Roadmap à venir
            </h3>
            <span className="text-xs opacity-50">{sections.aFaire.length} fonctionnalités</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.aFaire.map((card, i) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  className={`p-5 rounded-2xl ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-black/[0.02] border-black/5'} border`}
                  data-testid={`a-faire-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 opacity-60" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{card.title}</span>
                        <span className="text-[10px] uppercase tracking-widest opacity-50">{card.priority}</span>
                      </div>
                      <div className="text-xs opacity-60 mt-1">{card.detail}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Coûts — Gratuit */}
        <section className="mb-16" data-testid="section-gratuit">
          <div className="flex items-baseline gap-3 mb-2">
            <h3 className="text-2xl font-light">
              <Gift className="w-5 h-5 inline-block text-[#81d8d0] -mt-1 mr-1" />
              Gratuit & déjà en place
            </h3>
            <span className="text-xs opacity-50">{sections.gratuit.length} briques · 0 € à payer</span>
          </div>
          <p className="text-sm opacity-60 mb-6">
            Tout ce qui suit est déjà installé et fonctionne sans aucun frais récurrent. La V1 Beta tourne avec ça.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sections.gratuit.map((card, i) => (
              <div
                key={card.title}
                className={`p-5 rounded-2xl ${isDark ? 'bg-[#81d8d0]/[0.05] border-[#81d8d0]/20' : 'bg-[#81d8d0]/10 border-[#81d8d0]/30'} border`}
                data-testid={`gratuit-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#81d8d0]/20 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-[#81d8d0]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{card.title}</div>
                    <div className="text-xs opacity-70 mt-1">{card.detail}</div>
                    <div className="text-[11px] opacity-50 mt-2 italic">{card.note}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Coûts — Payant */}
        <section className="mb-16" data-testid="section-payant">
          <div className="flex items-baseline gap-3 mb-2">
            <h3 className="text-2xl font-light">
              <Euro className="w-5 h-5 inline-block text-amber-500 -mt-1 mr-1" />
              Intégrations à brancher (coûts à prévoir)
            </h3>
            <span className="text-xs opacity-50">{sections.payant.length} services tiers</span>
          </div>
          <p className="text-sm opacity-60 mb-6">
            Ces services nécessitent un compte chez le prestataire et, pour certains, un budget. La plupart proposent un palier gratuit pour démarrer.
          </p>
          <div className="grid grid-cols-1 gap-3">
            {sections.payant.map((card, i) => (
              <div
                key={card.title}
                className={`p-5 rounded-2xl ${isDark ? 'bg-amber-500/[0.04] border-amber-500/20' : 'bg-amber-50/60 border-amber-200'} border`}
                data-testid={`payant-${i}`}
              >
                <div className="flex flex-col md:flex-row md:items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                    <Euro className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{card.title}</div>
                    <div className="text-xs opacity-70 mt-1 mb-3">{card.detail}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                      <div className={`px-3 py-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                        <div className="opacity-50 uppercase tracking-widest text-[9px] mb-0.5">Coût</div>
                        <div className="font-medium">{card.cost}</div>
                      </div>
                      <div className={`px-3 py-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                        <div className="opacity-50 uppercase tracking-widest text-[9px] mb-0.5">Qui paie</div>
                        <div className="font-medium">{card.who}</div>
                      </div>
                      <div className={`px-3 py-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-white/80'}`}>
                        <div className="opacity-50 uppercase tracking-widest text-[9px] mb-0.5">Bloqué par</div>
                        <div className="font-medium">{card.blockedBy}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Footer note */}
        <div className={`text-center text-xs opacity-50 py-8 border-t ${isDark ? 'border-white/5' : 'border-black/5'}`}>
          Page mise à jour le 13/05/2026 — 49 tests automatisés passent ✓
        </div>
      </div>
      )}
    </div>
  );
};
